import React, { useEffect, useState } from 'react'

import { Card } from 'components'
import { useModal, useToggle, useLoading, usePersistForm } from 'hooks'
import moment from 'moment'
import { ViewContractModal } from 'pages/NGT/CRE/components/ViewContractModal/ViewContractModal'
import { useForm, FormProvider } from 'react-hook-form'
import {
  ConventionServices,
  IConvention,
  IConventionDetails,
  IFilterConvention
} from 'services/NGT/Convention'
import { FORMAT, SPACING, FUNCTIONS } from 'utils'
import { yupResolver, yup } from 'utils/yup'

import { FilterConventions, TableSearchConventions } from './components'
import EditConventionModal from './components/EditConventionModal/EditConventionModal'

const schema = yup.object().shape({
  dtvigenciaFim: yup.string().date().nullable().daterange({
    compare: 'dtvigenciaInicio',
    message: 'Data não pode ser menor que data vigência início',
    period: 'end'
  }),
  dtvigenciaInicio: yup.string().date().nullable().daterange({
    compare: 'dtvigenciaFim',
    message: 'Data não pode ser maior que data vigência fim',
    period: 'begin'
  })
})

const SearchConventionTable: React.FC = () => {
  const { setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const [openViewContractModal, toggleViewContractModal] = useToggle()
  const [openConventionEditModal, toggleConventionEditModal] = useToggle()
  const formMethods = useForm<IFilterConvention>({
    defaultValues: {
      cdContrato: '',
      cnpjFornecedor: '',
      cnpjSindicato: '',
      dtvigenciaFim: null,
      dtvigenciaInicio: null,
      flVencido: null,
      nmFornecedor: '',
      nmSindicato: '',
      pageNumber: 1,
      pageSize: 15
    },
    resolver: yupResolver(schema)
  })

  usePersistForm('acordo-convencao-ngt-form', {
    setValue: formMethods.setValue,
    watch: formMethods.watch
  })

  const [listConventions, setlistConventions] = useState<
    IConvention[] | undefined
  >()
  const [filtersClean, setFiltersClean] = useState(false)
  const [selectedContractId, setSelectedContractId] = useState<number>(0)
  const [selectedConvention, setSelectedConvention] = useState<
    IConventionDetails | undefined
  >()
  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [totalItems, setTotalItems] = useState<number>(0)

  const getConventionFilterData = async () => {
    setLoading('loading')

    ConventionServices()
      .getFiltro({
        ...FUNCTIONS.filterObject(formMethods.getValues(), Boolean),
        cnpjFornecedor: FORMAT.unMask(formMethods.getValues('cnpjFornecedor')),
        cnpjSindicato: FORMAT.unMask(formMethods.getValues('cnpjSindicato')),
        flVencido:
          formMethods.getValues('flVencido') !== 'Todos'
            ? eval(formMethods.getValues('flVencido'))
            : null,
        pageNumber: page,
        pageSize: rowsPerPage
      })
      .then(({ data }) => {
        setlistConventions(data.items)
        setTotalItems(data.pageCount)

        setLoading('idle')
      })
      .catch(() => {
        // setMsgErro('Erro ao carregar lista de Acordos e Convenções')
        setLoading('error')
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao carregar lista de Acordos e Convenções',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleSearchButton = () => {
    if (page == 1) {
      getConventionFilterData()
      return
    }

    setPage(1)
  }

  const handleSelectedConvention = (currentConvention) => {
    setLoading('loading')

    ConventionServices()
      .getById(currentConvention.idAcordoConvencao)
      .then(({ data }) => {
        setSelectedConvention(data)
        setLoading('idle')
        toggleConventionEditModal()
      })
      .catch(() => {
        // setMsgErro('Erro ao carregar dados do Acordo/Convenção')
        setLoading('error')
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao carregar dados do Acordo/Convenção',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleUpdateConvention = async (data: any) => {
    setLoading('loading')

    const idAcordoConvencao = data.idAcordoConvencao
    const requestObj = {
      cdHomologacao: data.cdHomologacao,
      contratoId: data.contratoId,
      dtVigenciaFim: moment(data.dtVigenciaFim).toISOString(),
      dtVigenciaInicio: moment(data.dtVigenciaInicio).toISOString(),
      flBancoHoras: eval(data.flBancoHoras),
      flHorasExtras: eval(data.flHorasExtras),
      flTurno: eval(data.flTurno),
      flValeAlimentacao: eval(data.flValeAlimentacao),
      flValeRefeicao: eval(data.flValeRefeicao),
      flVencido: data.flVencido,
      fornecedorId: data.fornecedorId,
      idsSindicato: [
        ...new Set(
          data.sindicatos
            .filter((sindicato) => Boolean(sindicato.idSindicato))
            .map((sindicato) => sindicato.idSindicato)
        )
      ],
      nuAnoMesCompetencia: [data.nuAnoMesCompetencia],
      peInsalubridade: Number(data.peInsalubridade),
      pePericulosidade: Number(data.pePericulosidade),
      pePisoSalarial: Number(data.pePisoSalarial),
      txObservacaoBancoHoras: data.txObservacaoBancoHoras,
      txObservacaoGeral: data.txObservacaoGeral,
      txObservacaoHorasExtras: data.txObservacaoHorasExtras,
      txObservacaoTurno: data.txObservacaoTurno,
      txTipo: data.txTipo,
      vlReajuste: Number(data.vlReajuste)
    }

    ConventionServices()
      .update(idAcordoConvencao, requestObj)
      .then((response) => {
        if (response.status === 200) {
          showModal({
            hideButtonCancel: true,
            message: 'Dados salvos com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })

          getConventionFilterData()
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível salvar os dados!',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })

    setSelectedConvention(undefined)
  }

  const handleViewContractModal = (contractId: number) => {
    setSelectedContractId(contractId)
    toggleViewContractModal()
  }

  const handleDeleteConvention = (idAcordoConvencao: number) => {
    setLoading('loading')

    ConventionServices()
      .del(idAcordoConvencao)
      .then((response) => {
        if (response.status === 200) {
          showModal({
            hideButtonCancel: true,
            message: 'Deletado com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
          getConventionFilterData()
        }
      })
      .catch((err) => {
        showModal({
          hideButtonCancel: true,
          message: err,
          type: 'error'
        })
        setLoading('error')
      })
      .finally(() => {
        setLoading('idle')
      })
  }

  useEffect(() => {
    if (filtersClean) {
      getConventionFilterData()
      setFiltersClean(false)
    }
  }, [filtersClean])

  useEffect(() => {
    getConventionFilterData()
  }, [page, rowsPerPage])

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <FormProvider {...formMethods}>
          <FilterConventions handleSearchConvention={handleSearchButton} />
          <TableSearchConventions
            tableData={listConventions}
            handleEditConvention={handleSelectedConvention}
            handleDeleteConvention={handleDeleteConvention}
            handleViewContractModal={handleViewContractModal}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            setPage={setPage}
            page={page}
            totalItems={totalItems}
          />
        </FormProvider>
      </Card>
      <EditConventionModal
        showEditModal={openConventionEditModal}
        toggleModal={toggleConventionEditModal}
        conventionData={selectedConvention}
        handleSubmitUpdate={handleUpdateConvention}
      />
      <ViewContractModal
        showModal={openViewContractModal}
        toggleModal={toggleViewContractModal}
        contractID={selectedContractId}
      />
    </>
  )
}

export default SearchConventionTable
